import { ButtonLooks, FontIcons, IconButton } from '@brandfolder/react';
import classNames from 'classnames';
import { FunctionComponent, useContext } from 'react';
import styled, { keyframes } from 'styled-components';

import { AsidePopout, AsidePopoutProps, HoverHelpPositions } from '@components/aside/aside-popout';
import { AsideContext } from '@context/aside';
import { AssetsContext } from '@context/assets';
import { DragAndDropContext } from '@context/drag-and-drop';
import { SyncContext } from '@context/sync';

import { ColorsList } from './colors-list';
import { ColorSettings } from './colors-settings';

type OmittedAsidePopoutProps = Omit<
  AsidePopoutProps,
  | 'content'
  | 'hoverHelpPosition'
  | 'icon'
  | 'id'
  | 'label'
  | 'open'
  | 'setOpen'
  | 'title'
  | 'tooltip'
>;

interface ColorsPopoutProps extends OmittedAsidePopoutProps {}

const UnstyledColorsPopout: FunctionComponent<ColorsPopoutProps> = (props) => {
  const { setShowColor, setShowColorSettings, showColor, showColorSettings } =
    useContext(AsideContext);
  const { errorColors, loadingColors } = useContext(AssetsContext);
  const { dragged } = useContext(DragAndDropContext);
  const { setSync, syncing } = useContext(SyncContext);

  const openOnHover = dragged ? false : true;

  return (
    <AsidePopout
      className={classNames({
        'aside-blocks-colors': true,
        'aside-blocks-colors--settings': showColorSettings,
        [`${props.className}`]: !!props.className
      })}
      content={showColorSettings ? <ColorSettings /> : <ColorsList />}
      icon={FontIcons.Palette}
      height={500}
      hoverHelpPosition={HoverHelpPositions.Both}
      id="aside-blocks-color"
      label="Open add a color block"
      onClose={() => {
        setTimeout(() => {
          setShowColorSettings(false);
        }, 300);
      }}
      open={showColor}
      openOnHover={openOnHover}
      setOpen={setShowColor}
      title={showColorSettings ? 'Color settings' : 'Colors'}
      titleAfter={
        showColorSettings ? undefined : (
          <>
            {!errorColors && (
              <IconButton
                className={classNames({
                  'aside-blocks-colors__title-icon': true,
                  'aside-blocks-colors__sync-colors': true,
                  'aside-blocks-colors__sync-colors--spin': syncing
                })}
                disabled={loadingColors || syncing}
                icon={FontIcons.Refresh}
                label="Sync colors"
                look={ButtonLooks.Default}
                onClick={() => {
                  setSync(true);
                }}
              />
            )}
            <IconButton
              className={classNames({
                'aside-blocks-colors__title-icon': true,
                'aside-blocks-colors__show-settings': true
              })}
              icon={FontIcons.Settings}
              label="Show color settings"
              look={ButtonLooks.Default}
              onClick={() => {
                setShowColorSettings(true);
              }}
            />
          </>
        )
      }
      titleBefore={
        showColorSettings ? (
          <IconButton
            className={classNames({
              'aside-blocks-colors__title-icon': true,
              'aside-blocks-colors__back': true
            })}
            icon={FontIcons.ArrowLeft}
            label="Back to colors list"
            look={ButtonLooks.Default}
            onClick={() => {
              setShowColorSettings(false);
            }}
          />
        ) : undefined
      }
      tooltip="Color"
    />
  );
};

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
`;

const StyledColorsPopout = styled(UnstyledColorsPopout)`
  &:not(.aside-blocks-colors--settings) {
    .bf-button__close {
      margin-left: 12px;
    }
  }

  .aside-blocks-colors__title-icon {
    height: 20px;
    min-height: 0;
    padding: 0;
    width: 20px;
  }

  .aside-blocks-colors__back {
    margin-right: 12px;
  }

  .aside-blocks-colors__sync-colors {
    margin-left: auto;

    + .aside-blocks-colors__show-settings {
      margin-left: 12px;
    }

    &.aside-blocks-colors__sync-colors--spin {
      .bf-icon__font {
        animation: ${spin} 1s linear infinite;
      }
    }
  }

  .aside-blocks-colors__show-settings {
    margin-left: auto;

    .bf-icon__font {
      font-size: 16px;
    }
  }

  .bf-popout__body {
    display: flex;
    height: 445px;
    overflow: hidden;
    padding: 0;

    .bf-popout__autofocus {
      display: flex;
      flex: 1;
    }
  }
`;

export const ColorsPopout: FunctionComponent<ColorsPopoutProps> = (props) => {
  return <StyledColorsPopout {...props} />;
};
